import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CircleNotch, Headset, User as UserIcon, X, ChatCircleDots } from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getApiErrorMessage } from '@/lib/api';
import { fetchMyTicket, replyToMyTicket, type MyTicket } from '@/lib/supportApi';

/**
 * The customer half of a support ticket conversation — one implementation, two places.
 *
 * `MySupportTicketPage` embeds it, and the row-level 💬 action on "My support tickets" opens it
 * in a drawer. Both read the SAME query key (`['support','mine',id]`) and call the SAME
 * endpoints (`GET /support/:id`, `POST /support/:id/replies`) that were already there — there is
 * no second conversation, no second table and no second API.
 *
 * It is the SAME `SupportTicket` thread the platform console shows. Internal Support notes are
 * filtered out server-side (the `internal: false` WHERE clause in getMyTicket) and never reach
 * this component, so there is no client-side filtering to get wrong. A ticket that is not yours
 * 404s inside the same query — this component cannot be made to render someone else's thread by
 * passing it a different id.
 */

function fmt(s: string) {
  return new Date(s).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** Shared so the page and the drawer resolve to ONE cache entry, never two fetches. */
export function useMyTicket(ticketId: string | undefined) {
  return useQuery<MyTicket>({
    queryKey: ['support', 'mine', ticketId],
    queryFn: () => fetchMyTicket(ticketId!),
    enabled: !!ticketId,
  });
}

export function MyTicketConversation({
  ticketId,
  autoFocusReply = false,
}: {
  ticketId: string;
  autoFocusReply?: boolean;
}) {
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');
  const replyRef = useRef<HTMLTextAreaElement>(null);

  const { data: ticket, isLoading, error } = useMyTicket(ticketId);

  useEffect(() => {
    if (autoFocusReply && ticket) replyRef.current?.focus();
  }, [autoFocusReply, ticket]);

  const replyMutation = useMutation({
    mutationFn: () => replyToMyTicket(ticketId, reply),
    onSuccess: (res) => {
      setReply('');
      toast.success(res.reopened ? 'Reply sent — the ticket has been reopened.' : 'Reply sent.');
      // Refreshes the thread AND the list, so the row's badge and status move together.
      queryClient.invalidateQueries({ queryKey: ['support', 'mine'] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not send the reply')),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <CircleNotch className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // A ticket that is not yours returns 404 — the server never confirms it exists.
  if (error || !ticket) {
    return (
      <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
        This support ticket could not be found.
      </CardContent></Card>
    );
  }

  return (
    <div>
      {/* The original report — the first thing in the conversation. */}
      <Card className="mb-4">
        <CardContent className="p-5">
          <div className="mb-2 flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <UserIcon className="h-3.5 w-3.5" /> You · {fmt(ticket.createdAt)}
          </div>
          <p className="whitespace-pre-wrap text-sm text-foreground">{ticket.body}</p>
        </CardContent>
      </Card>

      {ticket.messages.map((m) => (
        <Card key={m.id} className={`mb-4 ${m.authorType === 'support' ? 'border-primary/30' : ''}`}>
          <CardContent className="p-5">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs font-medium text-muted-foreground">
              {m.authorType === 'support'
                ? <><Headset className="h-3.5 w-3.5 text-primary" /> <span className="font-semibold text-foreground">{m.authorName ?? 'Jurivoxa Support'}</span></>
                : <><UserIcon className="h-3.5 w-3.5" /> {m.isMine ? 'You' : (m.authorName ?? 'You')}</>}
              <span>· {fmt(m.createdAt)}</span>
            </div>
            <p className="whitespace-pre-wrap text-sm text-foreground">{m.body}</p>
          </CardContent>
        </Card>
      ))}

      <Card>
        <CardContent className="p-5">
          <label htmlFor={`support-reply-${ticketId}`} className="mb-2 block text-xs font-medium text-muted-foreground">
            {ticket.status === 'closed' || ticket.status === 'resolved'
              ? 'Still having the problem? Reply to reopen this ticket.'
              : 'Add a reply'}
          </label>
          <textarea
            id={`support-reply-${ticketId}`}
            ref={replyRef}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={4}
            placeholder="Add anything else that might help…"
            className="w-full rounded-lg border border-border bg-background p-3 text-sm text-foreground focus:border-primary focus:outline-none"
          />
          <div className="mt-3 flex justify-end">
            <Button onClick={() => replyMutation.mutate()} disabled={!reply.trim() || replyMutation.isPending}>
              {replyMutation.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : 'Send reply'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── The drawer the row action opens ──────────────────────────────────────────

export function MyTicketConversationDrawer({
  ticketId,
  title,
  onClose,
}: {
  ticketId: string;
  title: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Conversation — ${title}`}
        className="relative flex h-full w-full max-w-xl flex-col border-l border-border bg-background shadow-pop"
      >
        <div className="flex items-start justify-between gap-3 border-b border-border p-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              <ChatCircleDots className="h-3.5 w-3.5" /> Conversation with support
            </div>
            <h3 className="mt-1 truncate text-base font-semibold text-foreground">{title}</h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close conversation"
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <MyTicketConversation ticketId={ticketId} autoFocusReply />
        </div>
      </div>
    </div>
  );
}

// ─── The row action ───────────────────────────────────────────────────────────

/**
 * Compact, row-level entry into the conversation for this exact ticket.
 *
 * The badge counts Support replies that arrived since the barrister's own last message — the
 * same fact the console's badge shows from the other end (conversationSignals). It is called
 * "new" rather than "unread" because there is no per-message read receipt in the schema and a
 * label the data cannot support is worse than no label.
 */
export function MyTicketConversationButton({
  unanswered,
  messageCount,
  onClick,
  className = '',
}: {
  unanswered: number;
  messageCount: number;
  onClick: () => void;
  className?: string;
}) {
  const label = unanswered > 0
    ? `Conversation — ${unanswered} new ${unanswered === 1 ? 'reply' : 'replies'} from support`
    : messageCount > 0
      ? `Conversation — ${messageCount} ${messageCount === 1 ? 'message' : 'messages'}`
      : 'Conversation — no replies yet';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`}
    >
      <ChatCircleDots className="h-5 w-5" weight={unanswered > 0 ? 'fill' : 'regular'} />
      {unanswered > 0 && (
        <span
          aria-hidden="true"
          className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground"
        >
          {unanswered > 9 ? '9+' : unanswered}
        </span>
      )}
    </button>
  );
}
